import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, Copy, Check, ArrowRight, Lock } from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../lib/supabase';
import { WALLS } from '../types/lanesAndDrivers';
import { cn } from '../utils/cn';
import { differenceInCalendarDays } from 'date-fns';

const LOCK_DAYS = 7;

export function AIPromptSection() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [prompt, setPrompt] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      setPrompt(generatePrompt());
    }
  }, [profile]);

  async function loadProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      setProfile(data);
    }
  }

  function getWallName(wallId: string | null): string {
    if (!wallId) return '';
    return WALLS.find(w => w.id === wallId)?.name || wallId;
  }

  function generatePrompt(): string {
    if (!profile) return '';

    const value1 = profile.core_value_1 || 'Value 1';
    const value2 = profile.core_value_2 || 'Value 2';
    const value3 = profile.core_value_3 || 'Value 3';

    const wall1 = getWallName(profile.lane_1) || 'Wall 1';
    const wall2 = getWallName(profile.lane_2) || 'Wall 2';
    const wall3 = getWallName(profile.lane_3) || 'Wall 3';
    const wall4 = getWallName(profile.lane_4) || 'Wall 4';

    const value1Desc = profile.value_1_description || `my personal definition of ${value1}`;
    const value2Desc = profile.value_2_description || `my personal definition of ${value2}`;
    const value3Desc = profile.value_3_description || `my personal definition of ${value3}`;

    const wall1Desc = profile.lane_1_description || `my vision for ${wall1}`;
    const wall2Desc = profile.lane_2_description || `my vision for ${wall2}`;
    const wall3Desc = profile.lane_3_description || `my vision for ${wall3}`;
    const wall4Desc = profile.lane_4_description || `my vision for ${wall4}`;

    return `Your core values are ${value1}, ${value2}, and ${value3}.

${value1} means ${value1Desc}.
${value2} means ${value2Desc}.
${value3} means ${value3Desc}.

Your walls and mission are to help build your foundation in ${wall1}, ${wall2}, ${wall3}, and ${wall4}.

${wall1} is ${wall1Desc}.
${wall2} is ${wall2Desc}.
${wall3} is ${wall3Desc}.
${wall4} is ${wall4Desc}.

______

REPLY INSTRUCTIONS:
Use this framework to solve problems and achieve goals. When a user presents a challenge, analyze which Wall needs strengthening. Provide a response that is daring, clear, and focused on immediate action. Use everyday language. No dashes. No icons.

IF THE USER APPEARS STUCK
Ask them if they would like to go through their core values. If they agree, ask them the questions below in order. Between each question, reply with a 1-2 sentence acknowledgement of their answer, and then ask the next question. Once they answer the last question, acknowledge their response and ask if they need help with that.

1. What can you celebrate about yesterday?
2. What did you learn from that?
3. What is your pressing goal?
4. What is the fear that comes up when you think of that goal?
5. What do your core values (${value1}, ${value2}, and ${value3}) say to do? Create a list.
6. From that list, what action can you take today?`;
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  const hasWalls = profile && profile.lane_1 && profile.lane_2 && profile.lane_3 && profile.lane_4;
  const isProfileLoaded = profile !== null;
  const hasPurchased = !!profile?.challenge_purchased_at;

  const signupDate = profile?.created_at ? new Date(profile.created_at) : null;
  const daysSinceSignup = signupDate ? differenceInCalendarDays(new Date(), signupDate) : 0;
  const isTimeLocked = isProfileLoaded && hasPurchased && daysSinceSignup < LOCK_DAYS;
  const isLocked = isProfileLoaded && (!hasPurchased || isTimeLocked);
  const daysUntilUnlock = isTimeLocked ? LOCK_DAYS - daysSinceSignup : 0;

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => isProfileLoaded && setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex justify-between items-center px-4 py-6 text-left",
          isProfileLoaded ? "hover:bg-blue-50 cursor-pointer" : "cursor-default opacity-60"
        )}
        disabled={!isProfileLoaded}
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <Sparkles className="w-5 h-5 text-[#004060]" />
          Your AI Prompt
          {isTimeLocked && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
              <Lock className="w-3 h-3" />
              Day 7
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {isExpanded && isProfileLoaded && (
        <div className="p-4 sm:p-6 space-y-4">
          {isLocked ? (
            <div className="rounded-xl bg-gradient-to-br from-[#004060] to-[#236caf] p-6 text-white space-y-4">
              <div className="flex items-center gap-3">
                <Lock className="w-6 h-6 text-blue-200" />
                <h3 className="text-lg font-semibold">
                  {hasPurchased ? 'Your AI Prompt Unlocks on Day 7' : 'Included with the Challenge'}
                </h3>
              </div>
              {hasPurchased ? (
                <>
                  <p className="text-blue-100 leading-relaxed text-sm">
                    On day seven your values based AI prompt unlocks. By then you will have a full week of the Refinement System working for you and you will know exactly how to use this tool to go even deeper.
                  </p>
                  <div className="bg-white/10 rounded-lg px-4 py-3 text-center">
                    <p className="text-blue-100 text-sm">Unlocks in</p>
                    <p className="text-3xl font-bold text-white mt-1">
                      {daysUntilUnlock} {daysUntilUnlock === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                  <p className="text-blue-200 text-sm">
                    In the meantime, keep going. Open your email each morning and do the thing.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-blue-100 leading-relaxed text-sm">
                    Your personalized AI prompt is included with the 21 Day Challenge. Join the challenge to unlock a fully values-aligned AI prompt you can use with any AI assistant.
                  </p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center bg-white text-[#004060] hover:bg-blue-50 font-semibold px-5 py-2.5 rounded-lg transition-colors w-full justify-center"
                  >
                    Join the 21 Day Challenge
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                </>
              )}
            </div>
          ) : hasWalls ? (
            <>
              <div className="text-sm text-gray-700 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p>
                  Copy this into your ChatGPT Custom Instructions, a Gemini Gem, or Claude's profile settings.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-gray-700">
                  {prompt}
                </pre>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleCopy}
                  variant="secondary"
                  className="flex-1"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Prompt
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => navigate('/ai-spark-prompt')}
                  className="flex-1"
                >
                  Edit Definitions
                </Button>
              </div>
            </>
          ) : (
            <div className="rounded-xl bg-gradient-to-br from-[#004060] to-[#236caf] p-6 text-white space-y-4">
              <h3 className="text-lg font-semibold">Unlock Your Full AI Prompt</h3>
              <p className="text-blue-100 leading-relaxed text-sm">
                Your core values are set. The next step is identifying your four focus areas and what your values and goals mean to you personally. This unlocks a fully personalized AI prompt you can use with any AI assistant to get values-aligned guidance.
              </p>
              <button
                onClick={() => navigate('/your-walls')}
                className="inline-flex items-center bg-white text-[#004060] hover:bg-blue-50 font-semibold px-5 py-2.5 rounded-lg transition-colors w-full sm:w-auto justify-center"
              >
                Complete Your Setup
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AIPromptSection;
